import React, { useRef, useState, useEffect, useContext } from 'react';
import { AlertTriangle, KeyRound } from 'lucide-react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { auth } from '../api/config';
import Loader from "../components/loader";
import { SHOW_TOAST } from '../constant/utils';
import { useNavigate } from 'react-router-dom';
import { getClientName, getClientId, getClientExams } from '../api/client';
import SwitchExamButton from '../components/SwitchExamButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { getThemeHoverBgColor, getThemeTextColor } from '../utils/themeUtils';
import { reactivateUser } from '../api/activation';
import { BundleContext } from '../context/BundleProvider';
import { GET_APP_NAME } from '../../app_constant';

const NotSubscribed = ({ theme, currentTheme }) => {
  const user = auth().currentUser;
  const navigate = useNavigate();
  const [isLoading, setIsloading] = useState(false);
  const elementRef = useRef(null);
  const [clientName, setClientName] = useState("");
  const [availableExams, setAvailableExams] = useState([]);
  const { selectedCertificate } = useContext(BundleContext);
  const examName = GET_APP_NAME(selectedCertificate);

  const handleRightClick = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    const el = elementRef.current;
    el?.addEventListener('contextmenu', handleRightClick);
    return () => el?.removeEventListener('contextmenu', handleRightClick);
  }, []);

  useEffect(() => {
    const fetchClientName = async () => {
      try {
        const name = await getClientName();
        setClientName(name);
      } catch (error) {
        console.error('Error fetching client name:', error);
        setClientName('your organization');
      }
    };
    
    fetchClientName();
  }, []);

  useEffect(() => {
    const fetchExams = async () => {
      try {
        const exams = await getClientExams();
        setAvailableExams(exams);
      } catch (error) {
        console.error('Error fetching exams:', error);
        setAvailableExams([]);
      }
    };
    
    fetchExams();
  }, []);

  const handleSubmit = async (value, { resetForm }) => {
    setIsloading(true);
    try {
      const result = await reactivateUser({
        clientId: await getClientId(),
        activation_code: value.activationCode
      });

      if (result.success) {
        SHOW_TOAST(result.message, "success", 5000);
        navigate("/prepare");
      } else {
        SHOW_TOAST(result.message, "error", 10000);
      }
    } catch (err) {
      SHOW_TOAST(err.message, "error", 10000);
    } finally {
      // keep loader visible a bit so user sees feedback
      setTimeout(() => setIsloading(false), 2000);
    }
  };

  // Theme color helper functions - similar to Header
  const getModalBg = () => {
    return currentTheme === 'light'
      ? theme?.background?.light
      : theme?.background?.dark;
  };

  const getTextColor = () => {
    return currentTheme === 'light'
      ? theme?.text?.light
      : theme?.text?.dark;
  };
  

  const getBorderColor = () => {
    return currentTheme === 'light'
      ? theme?.border?.light
      : theme?.border?.dark;
  };

  const getPrimaryButtonBg = () => {
    return currentTheme === 'light'
      ? `linear-gradient(to right, ${theme?.background?.gradientStart}, ${theme?.background?.gradientEnd})`
      : `linear-gradient(to right, ${theme?.background?.gradientStartDark}, ${theme?.background?.gradientEndDark})`;
  };

  const getSecondaryButtonBg = () => {
    return currentTheme === 'light'
      ? theme?.secondary?.leftLight
      : theme?.secondary?.leftDark;
  };

  const getAlertBg = () => {
    return theme?.color.yellow.main;
  };

  const getAlertIconColor = () => {
    return theme?.status?.warning;
  };

  const getSubtleTextColor = () => {
    return theme?.text?.muted;
  };

  const getInputBg = () => {
    return theme?.input?.background;
  };

  const getInputBorderColor = () => {
    return theme?.input?.border;
  };

  const getLinkColor = () => {
    return theme?.text?.link;
  };
  const buttonStyle = {
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease',
    color: getThemeTextColor(theme, currentTheme),
    borderRadius: '9999px', // Full rounded
  };

  return (
    <div
      ref={elementRef}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-md z-50"
    >
      <div className="relative flex flex-col items-center p-8 rounded-lg max-w-md mx-auto backdrop-blur-lg border shadow-lg transition-all duration-300"
           style={{ 
             backgroundColor: getModalBg(), 
             borderColor: getBorderColor(), 
             color: getTextColor() 
           }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-opacity-90 z-10 rounded-lg"
               style={{ backgroundColor: getModalBg() }}>
            <Loader />
          </div>
        )}

        <div className="p-3 rounded-full mb-4" style={{ backgroundColor: getAlertBg() }}>
          <AlertTriangle className="w-8 h-8" style={{ color: getAlertIconColor() }} />
        </div>

        {/* "signed in as" line */}
        <p className="text-sm mb-4" style={{ color: getSubtleTextColor() }}>
          You are signed in as "{user?.email || user?.uid}". If this is not you,{' '}
          <button
            onClick={() => {
              auth().signOut().then(() => navigate('/login'));
            }}
            className="underline"
            style={{ color: getLinkColor() }}
          >
            sign out
          </button>.
        </p>

        <h2 className="text-xl font-bold mb-2" style={{ 
          color: getTextColor()
        }}>
          Oops - Looks like you don't have access
        </h2>

        {/* Updated main copy with clearer message */}
        <p className="text-center mb-6" style={{ color: getSubtleTextColor() }}>
          Your Premium Access for {examName} may have expired or this exam may not be included in your current plan. Please contact the administrator at {clientName || 'your organization'} for assistance.
        </p>

        {/* Action buttons - new row with SwitchExamButton */}
        <div className="flex flex-col sm:flex-row w-full gap-4">
          {availableExams.length > 1 && (
            <div className="flex-1 border rounded-md overflow-hidden pt-2 pb-2" style={{ borderColor: getBorderColor() }}>
              <SwitchExamButton
                theme={theme}
                currentTheme={currentTheme}
              />
            </div>
          )}
          <div className="flex-1 border rounded-md overflow-hidden pt-2 pb-2" style={{ borderColor: getBorderColor() }}>
            <div className="flex flex-col items-center ">
              <button
                className="w-10 h-10 flex items-center justify-center"
                style={buttonStyle}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = getThemeHoverBgColor(theme, currentTheme);
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={() => {
                  auth().signOut().then(() => navigate('/login'));
                }}
              >
                <FontAwesomeIcon
                  icon={faSignOutAlt}
                  className="text-sm"
                  style={{ color: getThemeTextColor(theme, currentTheme) }}
                />
              </button>
              <span
                className="text-xs mt-1"
                style={{ color: getThemeTextColor(theme, currentTheme) }}
              >
                Sign Out
              </span>
            </div>
          </div>
        </div>
        


        <div className="w-full my-4" style={{ borderTop: `1px solid ${theme?.divider?.[currentTheme === 'light' ? 'light' : 'dark']}` }}></div>

        <Formik
          initialValues={{ activationCode: "" }}
          onSubmit={handleSubmit}
          validationSchema={Yup.object().shape({
            activationCode: Yup.string()
              .matches(
                /^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/,
                'Activation code must be in the format XXXX-XXXX-XXXX'
              )
              .required('Activation code is required'),
          })}
        >
          {({ values, errors, handleBlur, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit} className="w-full">
              
              {/* Updated label */}
              <div className="mb-4">
                <label
                  htmlFor="activationCode"
                  className="block text-sm font-medium mb-1"
                  style={{ color: getTextColor() }}
                >
                  Have an activation code for this exam?
                </label>

                <div className="flex items-center">
                  <div className="rounded-l-md p-2 border border-r-0" 
                       style={{ 
                         backgroundColor: getInputBg(),
                         borderColor: getInputBorderColor()
                       }}>
                    <KeyRound className="h-6 w-5" style={{ color: getSecondaryButtonBg() }} />
                  </div>
                  <input
                    type="text"
                    id="activationCode"
                    placeholder="xxxx-xxxx-xxxx"
                    className={`
                      flex-1 p-2 rounded-r-md border
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                      
                    `}
                     style={{ 
                      background: getPrimaryButtonBg(),
                      color: getTextColor(),
                      borderColor: getInputBorderColor()
                    }} 
                    value={values.activationCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                {errors.activationCode && (
                  <p className="mt-1 text-sm" style={{ color: theme?.color.red.main }}>{errors.activationCode}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out 
                           focus:outline-none focus:ring-2 disabled:opacity-50"
                style={{ 
                  background: getPrimaryButtonBg(),
                  color: theme?.button?.primary?.text,
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}
              >
                Activate Access
              </button>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NotSubscribed;